import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';
import { useState } from 'react';
import ContactForm from '../home/components/ContactForm';

export default function PricingPage() {
  const [isContactFormOpen, setIsContactFormOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('');

  const setupServices = [
    {
      name: 'Website Design & Build',
      description: 'A custom, mobile-friendly website designed around your business goals.',
      icon: 'ri-palette-line',
      gradient: 'from-purple-500 to-violet-500',
    },
    {
      name: 'Bookings & Payments Setup',
      description: 'We set up your booking flow and payment processing so customers can book and pay online with ease.',
      icon: 'ri-calendar-check-line',
      gradient: 'from-blue-500 to-cyan-500',
    },
    {
      name: 'Lead Capture & Email Setup',
      description: 'Capture enquiries and automate follow‑ups so no leads fall through the cracks.',
      icon: 'ri-mail-send-line',
      gradient: 'from-emerald-500 to-green-500',
    },
  ];

  const monthlyPlans = [
    {
      name: 'Maintain',
      price: '79',
      description:
        'Your website, professionally maintained and running smoothly — without you worrying about the technical side.',
      bestFor: 'Best for businesses that want peace of mind and a reliable, well-maintained website.',
      features: [
        'Ongoing security monitoring and essential updates',
        'Automated monthly backups',
        'Minor content edits (up to a clearly defined fair-use limit per month)',
        'Contact form and enquiry checks',
        'Email-based support',
      ],
      popular: false,
      gradient: 'from-slate-600 to-slate-700',
    },
    {
      name: 'Optimise',
      price: '179',
      description: 'A website that improves over time — designed to support growth, enquiries, and performance.',
      bestFor: 'Best for businesses actively using their website to generate leads and enquiries.',
      features: [
        'Everything in Maintain, plus:',
        'Monthly content and layout improvements',
        'SEO health checks and site structure optimisation',
        'Performance and conversion reviews',
        'Monthly summary of updates and recommendations',
        'Priority support response times',
      ],
      popular: true,
      gradient: 'from-purple-600 to-blue-600',
    },
    {
      name: 'Scale',
      price: '299',
      description: 'Ongoing optimisation focused on conversions, enquiries, and revenue — not just design.',
      bestFor: 'Best for businesses that want their website to actively support marketing and revenue growth.',
      features: [
        'Everything in Optimise, plus:',
        'Ongoing optimisation of key landing pages',
        'Content uploads and campaign updates',
        'Email campaign support (Mailchimp or similar platforms)',
        'Booking and enquiry flow optimisation',
      ],
      popular: false,
      gradient: 'from-blue-600 to-cyan-600',
    },
  ];

  const handlePlanClick = (planName: string, planPrice: string) => {
    setSelectedPlan(`${planName} - £${planPrice}/month`);
    setIsContactFormOpen(true);
  };

  const scrollToContact = () => {
    try {
      const navigate = window.REACT_APP_NAVIGATE;
      if (typeof navigate === 'function') {
        navigate('/');
        setTimeout(() => {
          const element = document.getElementById('free-audit');
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      }
    } catch (err) {
      console.error('Failed to navigate to contact section:', err);
    }
  };

  return (
    <>
      <Navigation />
      <section className="relative py-20 md:py-32 overflow-hidden min-h-screen bg-[#0a0a0a]">
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">
          {/* Header */}
          <div className="text-center mb-12 md:mb-20">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 md:mb-6">
              Simple, Clear Pricing
            </h1>
            <p className="text-base md:text-lg text-white/50 max-w-3xl mx-auto px-4">
              No confusing packages or hidden costs. Choose what you need and know exactly what
              you're paying for.
            </p>
          </div>

          {/* One‑Off Setup Services */}
          <div className="mb-16 md:mb-24">
            <div className="text-center mb-8 md:mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                One‑Off Setup Services
              </h2>
              <p className="text-lg text-gray-300">Paid once. Yours forever.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-6 md:mb-8">
              {setupServices.map((service, index) => (
                <div
                  key={index}
                  className="bg-white/5 backdrop-blur-sm rounded-2xl p-5 md:p-6 border border-gray-700/50"
                >
                  <div
                    className={`w-10 h-10 md:w-12 md:h-12 rounded-xl bg-gradient-to-r ${service.gradient} p-2.5 mb-4`}
                  >
                    <i
                      className={`${service.icon} text-lg md:text-xl text-white w-full h-full flex items-center justify-center`}
                    ></i>
                  </div>

                  <h3 className="text-lg md:text-xl font-semibold text-white mb-3">
                    {service.name}
                  </h3>
                  <p className="text-base text-gray-300 leading-relaxed">{service.description}</p>
                </div>
              ))}
            </div>

            {/* Combined Pricing Message */}
            <div className="text-center bg-gradient-to-r from-purple-600/10 to-blue-600/10 backdrop-blur-sm rounded-2xl p-5 md:p-6 border border-purple-500/20">
              <div className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent mb-2">
                Complete Package from £750
              </div>
            </div>
          </div>

          {/* Monthly Support Plans */}
          <div className="mb-12 md:mb-16">
            <div className="text-center mb-8 md:mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Monthly Website Support
              </h2>
              <p className="text-lg text-gray-300">
                Ongoing care, improvements, and peace of mind.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
              {monthlyPlans.map((plan, index) => (
                <div
                  key={index}
                  onClick={() => handlePlanClick(plan.name, plan.price)}
                  className={`relative bg-white/5 backdrop-blur-sm rounded-3xl p-6 md:p-8 border transition-all duration-300 hover:scale-105 cursor-pointer ${
                    plan.popular
                      ? 'border-purple-500/50 shadow-2xl shadow-purple-500/20 lg:scale-105'
                      : 'border-gray-700/50 hover:border-purple-500/30'
                  }`}
                >
                  {plan.popular && (
                    <div className="absolute -top-3 md:-top-4 left-1/2 transform -translate-x-1/2">
                      <div className="bg-gradient-to-r from-purple-600 to-blue-500 text-white px-4 md:px-6 py-1.5 md:py-2 rounded-full text-xs md:text-sm font-semibold whitespace-nowrap">
                        Most Popular
                      </div>
                    </div>
                  )}

                  <div className="mb-6">
                    <h3 className="text-xl md:text-2xl font-bold text-white mb-3">{plan.name}</h3>
                    <div className="flex items-baseline gap-1 mb-4">
                      <span className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                        £{plan.price}
                      </span>
                      <span className="text-gray-400">/month</span>
                    </div>
                    <p className="text-base text-gray-300 leading-relaxed mb-4">{plan.description}</p>
                    <p className="text-sm text-purple-300/80 italic">{plan.bestFor}</p>
                  </div>

                  <div className="h-px bg-gradient-to-r from-transparent via-purple-500/30 to-transparent mb-6"></div>

                  <div className="space-y-3 mb-8">
                    {plan.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-4 h-4 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center mt-0.5">
                          <i className="ri-check-line text-white text-xs"></i>
                        </div>
                        <span className="text-base text-gray-300 leading-relaxed">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePlanClick(plan.name, plan.price);
                    }}
                    className={`w-full py-3 rounded-xl font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer text-base ${
                      plan.popular
                        ? 'bg-gradient-to-r from-purple-600 to-blue-500 text-white hover:from-purple-500 hover:to-blue-400'
                        : 'bg-white/10 border border-white/20 text-white hover:bg-white/15 hover:border-purple-400/50'
                    }`}
                  >
                    Get Started
                  </button>
                </div>
              ))}
            </div>

            {/* Managed Plans Notice */}
            <div className="text-center mt-8">
              <p className="text-sm text-gray-400">
                All plans are fully managed. No long-term contracts. Cancel anytime.
              </p>
            </div>
          </div>

          {/* Optional Extras */}
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 md:p-8 border border-gray-700/30 mb-8 md:mb-12">
            <h3 className="text-xl font-bold text-white mb-4 text-center">
              Optional Extras
            </h3>
            <p className="text-base text-gray-300 text-center leading-relaxed">
              Additional services such as new landing pages, SEO content, or advanced automation can
              be quoted separately once we understand your needs.
            </p>
          </div>

          {/* Key Benefits */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-8 md:mb-12">
            <div className="text-center p-5 md:p-6">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line text-lg md:text-xl text-white"></i>
              </div>
              <h4 className="text-lg font-semibold text-white mb-2">No Long Contracts</h4>
              <p className="text-sm text-gray-400">
                Cancel your monthly plan anytime with 30 days notice.
              </p>
            </div>

            <div className="text-center p-5 md:p-6">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-eye-line text-lg md:text-xl text-white"></i>
              </div>
              <h4 className="text-lg font-semibold text-white mb-2">Clear Monthly Limits</h4>
              <p className="text-sm text-gray-400">
                Know exactly what's included in your plan each month.
              </p>
            </div>

            <div className="text-center p-5 md:p-6">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-r from-purple-500 to-violet-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-money-pound-circle-line text-lg md:text-xl text-white"></i>
              </div>
              <h4 className="text-lg font-semibold text-white mb-2">No Hidden Costs</h4>
              <p className="text-sm text-gray-400">
                The price you see is the price you pay. Always.
              </p>
            </div>
          </div>

          {/* Final Message */}
          <div className="text-center px-4">
            <h3 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-white to-purple-200 bg-clip-text text-transparent mb-2">
              Simple pricing. Clear limits. No hidden costs.
            </h3>
            <p className="text-base text-gray-400 mb-6 md:mb-8">
              Need something different?{' '}
              <span
                className="text-transparent bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text cursor-pointer hover:from-purple-300 hover:to-blue-300 transition-all duration-300 font-semibold"
                onClick={scrollToContact}
              >
                Let's discuss your specific needs
              </span>
            </p>
          </div>
        </div>
      </section>
      <Footer />
      <ContactForm 
        isOpen={isContactFormOpen} 
        onClose={() => setIsContactFormOpen(false)}
        selectedPlan={selectedPlan}
      />
    </>
  );
}
